import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getGuestBookings } from '../../lib/api';
import { Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export default function GuestBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const loadBookings = async (cursor = null) => {
    try {
      const response = await getGuestBookings({ limit: 20, cursor });
      
      if (cursor) {
        setBookings(prev => [...prev, ...response.data.items]);
      } else {
        setBookings(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      completed: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-slate-100 text-slate-500',
      refunded: 'bg-purple-100 text-purple-700'
    };
    return variants[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="guest-bookings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">My Bookings</h1>
          <p className="text-slate-500 mt-1">View and manage your reservations</p>
        </div>
        <Link to="/search">
          <Button className="btn-primary">
            <Calendar className="w-4 h-4 mr-2" />
            Book a Stay
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : bookings.length > 0 ? (
        <>
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Link key={booking.id} to={`/app/bookings/${booking.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <h3 className="font-medium text-slate-900 text-lg">
                          {booking.property_title}
                        </h3>
                        <p className="text-slate-500">
                          {format(new Date(booking.check_in), 'EEEE, MMM d')} → {format(new Date(booking.check_out), 'EEEE, MMM d, yyyy')}
                        </p>
                        <p className="text-sm text-slate-400">
                          {booking.nights} night{booking.nights !== 1 ? 's' : ''} • {booking.guests_count} guest{booking.guests_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusBadge(booking.status)}>
                          {booking.status}
                        </Badge>
                        <p className="text-lg font-semibold text-slate-900 mt-2">
                          {formatPrice(booking.total_charged)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => loadBookings(nextCursor)}
              >
                Load More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No bookings yet
            </h3>
            <p className="text-slate-500 mb-6">
              Start planning your Southwest Florida vacation today.
            </p>
            <Link to="/search">
              <Button className="btn-primary">
                Browse Properties
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
